import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Loader2, ChevronDown, ChevronRight, Users, Save, ArrowUpRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ApplyTargetRateDialog } from './ApplyTargetRateDialog';
import { MarginSparkline } from './MarginSparkline';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtA = (v) => (v == null ? '—' : `A$${Math.round(v).toLocaleString()}`);
const rate = (v) => (v == null ? '—' : `A$${Number(v).toFixed(0)}/h`);
const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

const STATUS = {
  under_priced: { label: 'Under-priced', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  on_target: { label: 'On target', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  premium: { label: 'Premium', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
};

/** Portfolio: is each role carrying or dragging margin across all open deals? */
export function RolePricingCard() {
  const { user } = useAuth();
  const canApplyTarget = ['cro', 'cco', 'super_admin'].includes(user?.role);
  const [target, setTarget] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [gapRates, setGapRates] = useState({});
  const [saving, setSaving] = useState(false);
  const [region, setRegion] = useState('');
  const [regions, setRegions] = useState(null);
  const [trend, setTrend] = useState(null);

  const load = (reg = region) => axios.get(`${API_URL}/api/insights/margin/role-pricing`, { params: reg ? { region: reg } : {}, withCredentials: true })
    .then((r) => { setData(r.data); setRegions(r.data.regions || []); })
    .catch((e) => setError(e?.response?.data?.detail || 'Could not load role pricing'));
  useEffect(() => { load(region); }, [region]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API_URL}/api/insights/margin/region-trend`, { params: { weeks: 8 }, withCredentials: true })
      .then((r) => setTrend(Object.fromEntries((r.data.regions || []).map((t) => [t.region, { ...t, weeks: r.data.weeks }]))))
      .catch(() => setTrend({}));
  }, []);
  const trendTitle = (t) => t?.points ? t.weeks.map((w, i) => `${w}: ${t.points[i] == null ? '—' : `${t.points[i].toFixed(1)}%`} (${t.deals[i]} deal${t.deals[i] === 1 ? '' : 's'})`).join('\n') : '';

  const pendingGaps = Object.fromEntries(Object.entries(gapRates).filter(([, v]) => v !== '' && Number(v) > 0).map(([k, v]) => [k, Number(v)]));
  const saveGaps = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API_URL}/api/insights/margin/role-pricing/cost-rates`, { rates: pendingGaps, region: region || null }, { withCredentials: true });
      toast.success(r.data.message);
      setGapRates({});
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save cost rates'); } finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="role-pricing-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div>
            <h3 className="text-sm font-semibold text-[#0F2040] flex items-center gap-1.5"><Users className="w-4 h-4 text-slate-400" /> Role pricing across the portfolio</h3>
            <p className="text-[11px] text-slate-400">Sell vs internal cost per role over {data?.deals ?? '—'} open deals{region ? ` in ${region}` : ''} (A$). Tells you which roles carry margin and which drag it{region ? ' where that regional rate card applies' : ''}.</p>
          </div>
          {data?.target_margin_pct != null && (
            <span className="text-[10px] text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 whitespace-nowrap" data-testid="role-pricing-target">
              Target {pct(data.target_margin_pct)} ({data.target_source === 'guardrail' ? 'min-margin guardrail' : 'portfolio average'}) · premium ≥ +{data.premium_band_pts} pts
            </span>
          )}
        </div>
        {(regions || []).some((r) => r.region !== '—') && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="region-margin-scorecard">
            {regions.filter((r) => r.region !== '—').map((r) => {
              const below = data?.target_margin_pct != null && r.margin_pct != null && r.margin_pct < data.target_margin_pct;
              const t = trend?.[r.region];
              return (
                <button key={r.region} type="button" onClick={() => setRegion(region === r.region ? '' : r.region)}
                  className={`text-[10px] rounded-md border px-2 py-1 text-left hover:bg-slate-50 ${region === r.region ? 'border-[#0F2040]' : 'border-slate-200'}`}
                  title={`${r.deals} open deal(s) · A$${Math.round(r.sell_aud).toLocaleString()} sell${t?.points ? `\n\n8-week margin trend\n${trendTitle(t)}` : ''}`} data-testid={`region-score-${r.region}`}>
                  <span className="font-semibold text-[#0F2040]">{r.region}</span>
                  <span className={`ml-1.5 font-semibold tabular-nums ${r.margin_pct == null ? 'text-slate-400' : below || r.margin_pct < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{r.margin_pct == null ? '—' : `${r.margin_pct.toFixed(1)}%`}</span>
                  <span className={`ml-1.5 ${r.below_guardrail > 0 ? 'text-rose-600 font-semibold' : 'text-slate-400'}`} data-testid={`region-score-breaches-${r.region}`}>{r.below_guardrail} breach{r.below_guardrail === 1 ? '' : 'es'}</span>
                  <span className="ml-1.5 text-slate-400">{r.deals} deal{r.deals === 1 ? '' : 's'}</span>
                  {t && (
                    <span className="ml-2 inline-flex items-center gap-1" data-testid={`region-trend-${r.region}`}>
                      <MarginSparkline points={t.points} trend={t.trend} testId={`region-sparkline-${r.region}`} />
                      {t.delta_pts != null && (
                        <span className={`tabular-nums ${t.trend === 'down' ? 'text-rose-600 font-semibold' : t.trend === 'up' ? 'text-emerald-700' : 'text-slate-400'}`} data-testid={`region-trend-delta-${r.region}`}>
                          {t.delta_pts > 0 ? '+' : ''}{t.delta_pts.toFixed(1)} pts · 8w
                        </span>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {(regions || []).length > 0 && (
          <div className="flex flex-wrap items-center gap-1 mt-2" role="tablist" data-testid="role-pricing-region-tabs">
            <button type="button" role="tab" aria-selected={region === ''} onClick={() => setRegion('')} data-testid="role-pricing-region-tab-all"
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${region === '' ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>All regions</button>
            {regions.filter((r) => r.region !== '—').map((r) => (
              <button key={r.region} type="button" role="tab" aria-selected={region === r.region} onClick={() => setRegion(r.region)} data-testid={`role-pricing-region-tab-${r.region}`}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${region === r.region ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                {r.region} <span className="opacity-60">{r.deals}</span>
              </button>
            ))}
            {regions.some((r) => r.region === '—') && <span className="text-[10px] text-slate-400 ml-1" data-testid="role-pricing-region-unassigned">{regions.find((r) => r.region === '—').deals} deal(s) without a region (global card)</span>}
          </div>
        )}
        {error && <p className="text-xs text-rose-600" data-testid="role-pricing-error">{error}</p>}
        {!data && !error && <div className="flex items-center gap-2 text-xs text-slate-400 py-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>}
        {data && !data.configured && <p className="text-xs text-amber-700" data-testid="role-pricing-not-configured">Set internal cost rates in Admin → Settings first.</p>}
        {data?.configured && data.roles.length === 0 && <p className="text-xs text-slate-400 mt-2" data-testid="role-pricing-empty">No open deals in {region || 'the portfolio'} yet.</p>}
        {data?.configured && data.roles.length > 0 && (
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs" data-testid="role-pricing-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="py-1.5 pr-2">Role</th>
                  <th className="py-1.5 pr-2 text-right">Deals</th>
                  <th className="py-1.5 pr-2 text-right">Hours</th>
                  <th className="py-1.5 pr-2 text-right">Avg sell</th>
                  <th className="py-1.5 pr-2 text-right">Cost rate</th>
                  <th className="py-1.5 pr-2 text-right">Sell (A$)</th>
                  <th className="py-1.5 pr-2 text-right">Margin (A$)</th>
                  <th className="py-1.5 pr-2 text-right">Margin %</th>
                  <th className="py-1.5 pr-2 text-right">vs target</th>
                  <th className="py-1.5 text-right" title="Sell rate that lands this role exactly on the target margin: cost ÷ (1 − target)">Target rate</th>
                </tr>
              </thead>
              <tbody>
                {data.roles.map((r) => {
                  const st = STATUS[r.status];
                  const isOpen = open === r.role;
                  return (
                    <React.Fragment key={r.role}>
                      <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(isOpen ? null : r.role)} data-testid={`role-pricing-row-${r.role}`}>
                        <td className="py-1.5 pr-2 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 font-semibold text-[#0F2040]">{isOpen ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}{r.role}</span>
                          <span className="text-slate-400 ml-1">{r.label}</span>
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{r.deals}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{r.hours.toLocaleString()}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{rate(r.avg_sell_rate_aud)}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums" onClick={(e) => e.stopPropagation()}>
                          {r.avg_cost_rate_aud != null ? rate(r.avg_cost_rate_aud) : data.can_edit_cost_rates ? (
                            <Input type="number" min="1" step="1" placeholder="A$/h" value={gapRates[r.role] ?? ''} onChange={(e) => setGapRates((g) => ({ ...g, [r.role]: e.target.value }))}
                              className="h-6 w-20 text-[11px] tabular-nums inline-block border-amber-300 bg-amber-50/40" data-testid={`role-pricing-gap-input-${r.role}`} />
                          ) : <span className="text-amber-600" title="No internal cost rate set">not set</span>}
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{fmtA(r.sell_aud)}</td>
                        <td className={`py-1.5 pr-2 text-right tabular-nums font-semibold ${r.margin_aud == null ? 'text-slate-400' : r.margin_aud < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{fmtA(r.margin_aud)}</td>
                        <td className={`py-1.5 pr-2 text-right tabular-nums font-semibold ${r.margin_pct == null ? 'text-slate-400' : r.margin_pct < 0 ? 'text-rose-600' : r.margin_pct < 20 ? 'text-amber-600' : 'text-emerald-700'}`} data-testid={`role-pricing-margin-${r.role}`}>{pct(r.margin_pct)}</td>
                        <td className="py-1.5 pr-2 text-right">
                          {st ? (
                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded-full px-2 py-0.5 ${st.cls}`} data-testid={`role-pricing-status-${r.role}`}>
                              {st.label}<span className="font-normal">{r.gap_pts > 0 ? '+' : ''}{r.gap_pts} pts</span>
                            </span>
                          ) : <span className="text-[10px] text-slate-400">—</span>}
                        </td>
                        <td className="py-1.5 text-right tabular-nums whitespace-nowrap" data-testid={`role-pricing-recommended-${r.role}`}>
                          {r.recommended ? (
                            <span title={`Landing ${r.role} on ${pct(data.target_margin_pct)} margin at today's cost rate${r.recommended.margin_impact_aud != null ? ` · ≈${r.recommended.margin_impact_aud >= 0 ? '+' : '−'}A$${Math.abs(Math.round(r.recommended.margin_impact_aud)).toLocaleString()} margin across ${r.deals} deal(s)` : ''}`}>
                              <span className="font-semibold text-[#0F2040]">{rate(r.recommended.rate_aud)}</span>
                              {r.recommended.vs_avg_sell_pct != null && Math.abs(r.recommended.vs_avg_sell_pct) >= 0.5 && (
                                <span className={`ml-1 text-[10px] font-semibold ${r.recommended.vs_avg_sell_pct > 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{r.recommended.vs_avg_sell_pct > 0 ? '+' : ''}{r.recommended.vs_avg_sell_pct}%</span>
                              )}
                              <button type="button" onClick={(e) => { e.stopPropagation(); setTarget({ role: r.role, rate: r.recommended.rate_aud, label: r.label }); }}
                                className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#4ABFCF] hover:text-[#0F2040] hover:underline" title="Preview affected deals and push this rate into the global rate card" data-testid={`role-pricing-apply-target-${r.role}`}>
                                Apply <ArrowUpRight className="w-3 h-3" />
                              </button>
                            </span>
                          ) : <span className="text-[10px] text-slate-400">—</span>}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/60" data-testid={`role-pricing-groups-${r.role}`}>
                          <td colSpan={10} className="px-4 py-2">
                            <div className="flex flex-wrap gap-2">
                              {r.groups.map((g) => (
                                <div key={g.group} className="border border-slate-200 bg-white rounded-lg px-2.5 py-1.5 min-w-[150px]" data-testid={`role-pricing-group-${r.role}-${g.group.replace(/[^\w]+/g, '-').toLowerCase()}`}>
                                  <div className="text-[10px] font-semibold text-slate-500">{g.group}</div>
                                  <div className="text-[11px] tabular-nums text-slate-600">{g.hours.toLocaleString()}h · {fmtA(g.sell_aud)} sell</div>
                                  <div className={`text-xs font-bold tabular-nums ${g.margin_pct == null ? 'text-slate-400' : g.margin_pct < 0 ? 'text-rose-600' : g.margin_pct < 20 ? 'text-amber-600' : 'text-emerald-700'}`}>{pct(g.margin_pct)} margin</div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {data.roles_missing_cost_rate?.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 mt-1.5">
                <p className="text-[10px] text-amber-700" data-testid="role-pricing-missing">
                  No internal cost rate for {data.roles_missing_cost_rate.join(', ')} — {data.can_edit_cost_rates ? `type an A$/h cost in the Cost rate column and save to price these roles (saved to the ${region ? region + ' regional' : 'global'} cost rate card).` : 'ask an admin to add them in Admin → Settings.'}
                </p>
                {data.can_edit_cost_rates && (
                  <Button size="sm" className="h-7 text-xs bg-[#0F2040] hover:bg-[#1a2f57] text-white" onClick={saveGaps} disabled={saving || Object.keys(pendingGaps).length === 0} data-testid="role-pricing-gap-save-btn">
                    <Save className="w-3 h-3 mr-1" />{saving ? 'Saving…' : `Save ${Object.keys(pendingGaps).length || ''} cost rate${Object.keys(pendingGaps).length === 1 ? '' : 's'}`}
                  </Button>
                )}
              </div>
            )}
            <p className="text-[10px] text-slate-400 mt-1" data-testid="role-pricing-recommendation-note">
              Target rate = cost rate ÷ (1 − target margin){data.target_source === 'portfolio' ? ' — set a min-margin guardrail in Admin → Settings to anchor recommendations to policy rather than the portfolio average' : ''}. Red % = uplift needed vs today's average sell rate; green = room to discount.
            </p>
          </div>
        )}
      </CardContent>
      {target && (
        <ApplyTargetRateDialog open={!!target} role={target.role} rate={target.rate} label={target.label} canApply={canApplyTarget} initialRegion={region}
          onClose={() => setTarget(null)} onApplied={() => load()} />
      )}
    </Card>
  );
}
